import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const WishesForm = () => {
  const authToken = useSelector((state) => state.auth.authToken);
  const [loading, setLoading] = useState(true);
  const [timeExceeded, setTimeExceeded] = useState(false);
  const [wishes, setWishes] = useState([]);
  const [exactTime, setExactTime] = useState(null);
  const navigate = useNavigate();
  const suggestions = ["Bicycle", "Watch", "Bike", "PS5", "Laptop", "Phone"];

  useEffect(() => {
    const fetchLastUpdate = async () => {
      try {
        const response = await axios.get("https://api-wish3s.vercel.app/data/lastupdate", {
          headers: { Authorization: `Bearer ${authToken}` },
          cache: "no-store",
        });

        const lastUpdate = response.data || 0;

        const updateRemainingTime = () => {
          const currentTime = Date.now();
          const diffInMs = currentTime - new Date(lastUpdate).getTime();
          const diffInHours = diffInMs / (1000 * 60 * 60);

          if (diffInHours >= 24) {
            setTimeExceeded(true);
          } else {
            const remainingMs = 24 * 60 * 60 * 1000 - diffInMs;

            const hours = Math.floor(remainingMs / (1000 * 60 * 60));
            const minutes = Math.floor((remainingMs % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((remainingMs % (1000 * 60)) / 1000);

            const nextUpdate = new Date(lastUpdate + 24 * 60 * 60 * 1000);

            setExactTime({
              hours,
              minutes,
              seconds,
              nextUpdate: nextUpdate.toLocaleTimeString("en-US"),
            });
          }
        };

        updateRemainingTime();
        const timer = setInterval(updateRemainingTime, 1000);
        setLoading(false);
        return () => clearInterval(timer);
      } catch (error) {
        console.error("Error fetching last update time:", error);
        setLoading(false);
      }
    };

    fetchLastUpdate();
  }, [authToken]);

  const handleSuggestionClick = (suggestion) => {
    setWishes((prevWishes) => {
      const index = prevWishes.findIndex((wish) => wish === "");
      if (index !== -1) {
        const updatedWishes = [...prevWishes];
        updatedWishes[index] = suggestion;
        return updatedWishes;
      }
      return prevWishes;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      await axios.post("https://api-wish3s.vercel.app/data/renewwishes", wishes, {
        headers: { Authorization: `Bearer ${authToken}` },
        cache: "no-store",
      });
      navigate("/");
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="loader ease-linear rounded-full border-4 border-t-4 border-gray-200 h-12 w-12"></div>
      </div>
    );
  }

  if (timeExceeded) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 text-gray-800 px-4">
        <h2 className="text-3xl font-bold mb-6">Update Your Wishes</h2>
        <form onSubmit={handleSubmit} className="w-full max-w-md bg-white shadow-lg rounded-lg p-6">
          {["wish1", "wish2", "wish3"].map((wish, index) => (
            <div className="mb-4" key={wish}>
              <label htmlFor={wish} className="block text-sm font-medium text-gray-700">
                Wish {index + 1}
              </label>
              <input
                type="text"
                name={wish}
                onChange={(e) =>
                  setWishes((prevWishes) => ({
                    ...prevWishes,
                    [wish]: e.target.value,
                  }))
                }
                className="mt-1 p-2 w-full border border-gray-300 rounded focus:ring focus:ring-red-400 focus:outline-none"
                required
              />
            </div>
          ))}
          <div className="flex flex-wrap gap-2 mb-4">
            {suggestions.map((suggestion, index) => (
              <button
                key={index}
                type="button"
                className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600"
                onClick={() => handleSuggestionClick(suggestion)}
              >
                {suggestion}
              </button>
            ))}
          </div>
          <button
            type="submit"
            className="w-full py-2 bg-red-500 text-white font-semibold rounded shadow hover:bg-red-600 focus:outline-none focus:ring focus:ring-red-400"
          >
            Submit
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 text-gray-800 px-4">
      <div className="w-full max-w-md bg-white/80 backdrop-blur-md shadow-lg rounded-lg p-6 text-center">
        <h2 className="text-xl font-bold">Time Remaining To Wish Again</h2>
        <p className="text-lg mt-2">
          {exactTime?.hours}h {exactTime?.minutes}m {exactTime?.seconds}s
        </p>
        <p className="text-sm text-gray-500 mt-2">
          You can make new wishes again tomorrow at {exactTime?.nextUpdate}.
        </p>
      </div>
      {wishes.length > 0 && (
        <div className="w-full max-w-md bg-white shadow-lg rounded-lg p-6 mt-6">
          <h3 className="text-lg font-bold mb-4">Your Wishes</h3>
          <ul className="list-disc list-inside">
            {wishes.map((wish, index) => (
              <li key={index} className="text-gray-700">
                {wish.wish} (Count: {wish.count})
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default WishesForm;
